import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  Calendar,
  Clock,
  Briefcase,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  User,
  Building2,
  Timer,
  HelpCircle,
  Home,
  Video
} from "lucide-react";

const InterviewInvitations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [startingVideoCall, setStartingVideoCall] = useState(null);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${API}/ai/interview/invitations`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (invitationId, response) => {
    setResponding(invitationId);
    try {
      const res = await fetch(`${API}/ai/interview/invitations/${invitationId}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ response }),
      });

      if (res.ok) {
        const data = await res.json();
        if (response === "accept") {
          toast.success("Interview accepted! You can now begin.");
          // Navigate to interview
          navigate(`/interview/${data.session_id}`);
        } else {
          toast.info("Interview declined");
          fetchInvitations();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to respond");
      }
    } catch (error) {
      toast.error("Failed to respond to invitation");
    } finally {
      setResponding(null);
    }
  };

  const handleStartVideoCall = async (invitation) => {
    setStartingVideoCall(invitation.invitation_id);
    try {
      // Create or get existing video room
      const res = await fetch(`${API}/video-calls/create-room`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          interview_id: invitation.invitation_id,
          candidate_id: invitation.candidate_id || invitation.user_id,
          duration_minutes: invitation.time_limit_minutes || 60
        }),
      });

      if (res.ok) {
        const data = await res.json();
        toast.success("Video call room ready!");
        navigate(`/video-call/${invitation.invitation_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create video call");
      }
    } catch (error) {
      toast.error("Failed to start video call");
    } finally {
      setStartingVideoCall(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "accepted": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "declined": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "expired": return "bg-gray-500/20 text-gray-400 border-gray-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <Clock className="w-4 h-4" />;
      case "accepted": return <CheckCircle className="w-4 h-4" />;
      case "declined": return <XCircle className="w-4 h-4" />;
      case "expired": return <AlertCircle className="w-4 h-4" />;
      default: return <HelpCircle className="w-4 h-4" />;
    }
  };

  const dashboardLink = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="font-heading text-3xl font-bold mb-2">
              {user?.role === "jobseeker" ? (
                <>INTERVIEW <span className="text-crimson">INVITATIONS</span></>
              ) : (
                <>SENT <span className="text-crimson">INVITATIONS</span></>
              )}
            </h1>
            <p className="text-gray-400">
              {user?.role === "jobseeker" 
                ? "Review and respond to interview invitations from recruiters"
                : "Track the status of interview invitations you've sent"
              }
            </p>
          </motion.div>

          {invitations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-obsidian-light border border-[#222] p-12 text-center"
            >
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Invitations</h3>
              <p className="text-gray-500">
                {user?.role === "jobseeker"
                  ? "You haven't received any interview invitations yet."
                  : "You haven't sent any interview invitations yet."
                }
              </p>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {invitations.map((inv, index) => (
                <motion.div
                  key={inv.invitation_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-obsidian-light border border-[#222] p-6 hover:border-crimson/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading text-xl font-bold">{inv.job_title}</h3>
                        <span className={`px-2 py-1 text-xs uppercase tracking-wider flex items-center gap-1 border ${getStatusColor(inv.status)}`}>
                          {getStatusIcon(inv.status)}
                          {inv.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {user?.role === "jobseeker" ? (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            From: {inv.recruiter_name || "Recruiter"}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            To: {inv.candidate_name || "Candidate"}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(inv.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Interview Details */}
                  <div className="flex items-center gap-6 mb-4 p-3 bg-obsidian border border-[#1a1a1a]">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-crimson" />
                      <span className="text-sm">
                        <span className="text-gray-500">Questions:</span>{" "}
                        <span className="text-white font-bold">{inv.num_questions}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Timer className="w-4 h-4 text-crimson" />
                      <span className="text-sm">
                        <span className="text-gray-500">Time Limit:</span>{" "}
                        <span className="text-white font-bold">{inv.time_limit_minutes} min</span>
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  {user?.role === "jobseeker" && inv.status === "pending" && (
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleRespond(inv.invitation_id, "accept")}
                        disabled={responding === inv.invitation_id}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`accept-${inv.invitation_id}`}
                      >
                        {responding === inv.invitation_id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" /> Accept
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleRespond(inv.invitation_id, "decline")}
                        disabled={responding === inv.invitation_id}
                        variant="outline"
                        className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`decline-${inv.invitation_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" /> Decline
                      </Button>
                    </div>
                  )}

                  {inv.status === "accepted" && inv.session_id && (
                    <div className="flex gap-3">
                      <Button
                        onClick={() => navigate(`/interview/${inv.session_id}`)}
                        className="flex-1 bg-crimson hover:bg-crimson-hover text-white"
                        data-testid={`start-interview-${inv.invitation_id}`}
                      >
                        <Play className="w-4 h-4 mr-2" /> 
                        {user?.role === "jobseeker" ? "Start AI Interview" : "View AI Interview"}
                      </Button>
                      {user?.role === "recruiter" && (
                        <Button
                          onClick={() => handleStartVideoCall(inv)}
                          disabled={startingVideoCall === inv.invitation_id}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`start-video-call-${inv.invitation_id}`}
                        >
                          {startingVideoCall === inv.invitation_id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Video className="w-4 h-4 mr-2" /> Start Video Call
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}

                  {inv.status === "expired" && (
                    <p className="text-gray-500 text-sm text-center">
                      This invitation has expired
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default InterviewInvitations;
